import { ColumnsType } from "antd/lib/table";
import { CustomerObjectMapType } from "../../../util/providers/CustomerProvider";
import { formatDate } from "../../../util";
import { Button, Space, Tag, Tooltip } from "antd";
import React from "react";
import History from "history";
import { FilterDropdownProps } from "antd/lib/table/interface";
import {
  CheckCircleFilled,
  CloseCircleFilled,
  MinusOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { Customer } from "../../../models/Customer";
import { Business } from "../../../models/Business";
import { FirebaseObject } from "../../../models/FirebasePostObject";
import { add, differenceInDays } from "date-fns";
import Stripe from "stripe";

export enum GiftStatus {
  Accepted = "Accepted",
  Pending = "Pending",
  Some = "Some",
  NoGifts = "No gifts",
}

type TableEntryType = Pick<
  Customer,
  "email" | "familyName" | "givenName" | "userStatus" | "phoneNumber" | "sub"
> & {
  business?: FirebaseObject<Business>;
};

export type ColumnFilters = {
  history: History.History;
  filters: {
    buildFilterDropdown: (
      dataIndex: string
    ) => (props: FilterDropdownProps) => JSX.Element;
    filteredInfo: Record<string, any>;
    sortedInfo: Record<string, any>;
  };
  handleToggleInvoiced: ({ code, sub }: { code: string; sub: string }) => void;
};

export const buildColumns = ({
  history,
  filters,
  handleToggleInvoiced,
}: ColumnFilters) => {
  const { buildFilterDropdown, filteredInfo, sortedInfo } = filters;

  const columns: ColumnsType<CustomerObjectMapType> = [
    {
      title: "Email",
      dataIndex: "email",
      filteredValue: filteredInfo.email || null,
      filterDropdown: buildFilterDropdown("email"),
      filterIcon: (filtered: any) => (
        <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
      ),
      onFilter: (
        value: string | number | boolean,
        record: TableEntryType
      ): boolean => {
        return record.email
          ? record.email
              .toString()
              .toLowerCase()
              .includes(value as string)
          : false;
      },
      key: "email",
      width: 400,
      sorter: (a, b) => {
        if (a.email > b.email) {
          return -1;
        }
        if (b.email > a.email) {
          return 1;
        }
        return 0;
      },
      sortOrder: sortedInfo.columnKey === "email" && sortedInfo.order,
      render: (value: string) => {
        const sanitizedEmail = value.toLowerCase();
        return sanitizedEmail;
      },
    },
    {
      title: "Name",
      key: "name",
      filteredValue: filteredInfo.name || null,
      filterDropdown: buildFilterDropdown("name"),
      filterIcon: (filtered: any) => (
        <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
      ),
      onFilter: (
        value: string | number | boolean,
        record: TableEntryType
      ): boolean => {
        const fullName = `${record.givenName} ${record.familyName}`;
        return fullName.includes(value as string);
      },
      responsive: ["lg"],
      sorter: (a, b) => {
        const nameA = a.hasCompletedSetup
          ? `${a.givenName} ${a.familyName}`.toLowerCase()
          : "Unknown";
        const nameB = b.hasCompletedSetup
          ? `${b.givenName} ${b.familyName}`.toLowerCase()
          : "Unknown";

        if (nameA > nameB) {
          return -1;
        }
        if (nameB > nameA) {
          return 1;
        }
        return 0;
      },
      sortOrder: sortedInfo.columnKey === "name" && sortedInfo.order,
      render: (_value, record) => {
        const name = record.hasCompletedSetup
          ? `${record.givenName} ${record.familyName}`
          : "Unknown";
        return name;
      },
    },
    {
      title: "Subscription", // Active or Cancelled (by checking 'sub' value on customer)
      key: "status",
      sorter: (a, b) => {
        const businessA = a.businesses ? a.businesses[0] : null;
        const isActiveA = businessA?.stripeSubscriptionId;
        const businessB = b.businesses ? b.businesses[0] : null;
        const isActiveB = businessB?.stripeSubscriptionId;

        if (isActiveA && !isActiveB) return -1;
        if (!isActiveA && isActiveB) return 1;
        return 0;
      },
      sortDirections: ["ascend", "descend"],
      sortOrder: sortedInfo.columnKey === "status" && sortedInfo.order,
      filteredValue: filteredInfo.status || null,
      filters: [
        { text: "Trialing", value: "trialing" },
        { text: "Active", value: "active" },
        { text: "Cancelling", value: "cancelling" },
        { text: "Canceled", value: "canceled" },
        { text: "Demo", value: "demo" },
      ],
      onFilter: (value, record) => {
        const business = record.businesses ? record.businesses[0] : null;
        const subscription = business?.subscription;

        if (
          value === "cancelling" &&
          ["active", "trialing"].includes(subscription?.status || "") &&
          subscription?.cancel_at
        )
          return true;

        if (
          value === "active" &&
          subscription?.status === "active" &&
          !subscription.cancel_at
        )
          return true;

        if (
          value === "trialing" &&
          subscription?.status === "trialing" &&
          !subscription.cancel_at
        )
          return true;

        if (
          value === "canceled" &&
          (["canceled", "ended"].includes(subscription?.status || "") ||
            (business?.stripeSubscriptionId && !subscription))
        )
          return true;

        if (
          value === "demo" &&
          !subscription &&
          !business?.stripeSubscriptionId
        )
          return true;

        return false;
      },
      responsive: ["lg"],
      render: (_value: string, record: CustomerObjectMapType) => {
        const business = record.businesses ? record.businesses[0] : null;
        const subscription = business?.subscription;

        // If the subscription is cancelled, the list doesn't return it, but we do have a subscription id present.
        // The lambda timesout if we tell it to include 'all' subscription statuses, so this is or only option.
        const isDemo = !business?.stripeSubscriptionId && !subscription;
        const isCancelled =
          ["canceled", "ended"].includes(subscription?.status || "") ||
          (business?.stripeSubscriptionId && !subscription);
        const isActive = ["active", "trialing"].includes(
          subscription?.status || ""
        ); /* legacy subscription status 'trialing'. we don't offer trials any more */
        const isCancelling = !!subscription?.cancel_at;
        const price = subscription?.items.data[0].price;
        const product = price?.product as Stripe.Product;
        const isInvoiced =
          product?.metadata.gift_eligible === "1" ||
          price?.metadata.trade === "true";

        let tooltipText, color, label: string;

        if (isDemo) {
          /* Demo - no subscription */
          tooltipText = "Demo customer";
          color = "blue";
          label = "Demo";
        } else if (isCancelled) {
          /* Cancelled - has had a subscription but is cancelled */
          tooltipText = `${subscription?.plan.name} - Cancelled`;
          color = "red";
          label = isInvoiced ? "Invoiced (cancelled)" : "Direct (cancelled)";
        } else if (isActive) {
          /* Active cancelling - has live subscription but set to cancel in future */
          tooltipText = `${subscription?.plan.name}${
            isCancelling
              ? `- Cancelling on ${formatDate(
                  (subscription?.cancel_at || 0) * 1000
                )}`
              : ""
          }`;
          color = "green";

          if (isInvoiced) {
            label = "Invoiced";
            if (isCancelling) {
              color = "orange";
              label += " (cancelling)";
            }
          } else {
            label = "Direct";
            if (isCancelling) {
              color = "orange";
              label += " (cancelling)";
            }
          }
        } else {
          /* Unexpect subscription status */
          tooltipText = "Unexpected subscription status";
          color = "purple";
          label = "Unknown";
        }

        return (
          <Tooltip title={tooltipText}>
            <Tag color={color}>{label}</Tag>
          </Tooltip>
        );
      },
    },
    {
      title: "Sub created date",
      key: "created",
      responsive: ["lg"],
      sorter: (a, b) => {
        const businessA = a.businesses ? a.businesses[0] : null;
        const subscriptionA = businessA?.subscription;
        const dateA = subscriptionA?.created || 0;

        const businessB = b.businesses ? b.businesses[0] : null;
        const subscriptionB = businessB?.subscription;
        const dateB = subscriptionB?.created || 0;

        return dateA > dateB ? -1 : 1;
      },
      sortDirections: ["ascend", "descend"],
      sortOrder: sortedInfo.columnKey === "created" && sortedInfo.order,
      render: (_value, record) => {
        const business = record.businesses ? record.businesses[0] : null;
        const subscription = business?.subscription;
        const date = subscription
          ? formatDate(subscription.created * 1000)
          : "";
        return date;
      },
    },
    {
      title: "Next billing date",
      key: "billing",
      responsive: ["lg"],
      filteredValue: filteredInfo.billing,
      filters: [
        { text: "7 days", value: 7 },
        { text: "14 days", value: 14 },
        { text: "30 days", value: 30 },
        { text: "60 days", value: 60 },
      ],
      filterMultiple: false,
      onFilter: (
        value: string | number | boolean,
        record: CustomerObjectMapType
      ): boolean => {
        const business = record.businesses ? record.businesses[0] : null;
        const subscription = business?.subscription;

        if (subscription) {
          const filterEndDate = add(new Date(), { days: Number(value) });
          const { current_period_end } = subscription;
          const subEndDate = new Date(current_period_end * 1000);
          const days = differenceInDays(filterEndDate, subEndDate);

          return days >= 0 && days <= Number(value);
        } else {
          return false;
        }
      },
      render: (_value, record) => {
        const business = record.businesses ? record.businesses[0] : null;
        const subscription = business?.subscription;

        if (subscription) {
          const { current_period_end } = subscription;
          const subscriptionBillingDate = formatDate(current_period_end * 1000);
          return subscriptionBillingDate;
        } else {
          return "";
        }
      },
    },
    {
      title: "Invoiced",
      key: "invoiced",
      fixed: "right",
      width: 100,
      sorter: (a, b) => {
        const businessA = a.businesses ? a.businesses[0] : null;
        const subscriptionA = businessA?.subscription;
        const priceA = subscriptionA?.items.data[0].price;
        const isTradeCustomerA = priceA?.metadata.trade === "true";

        const businessB = b.businesses ? b.businesses[0] : null;
        const subscriptionB = businessB?.subscription;
        const priceB = subscriptionB?.items.data[0].price;
        const isTradeCustomerB = priceB?.metadata.trade === "true";

        if (isTradeCustomerA || !isTradeCustomerB) return -1;
        if (!isTradeCustomerA || isTradeCustomerB) return 1;
        return 0;
      },
      sortDirections: ["ascend", "descend"],
      sortOrder: sortedInfo.columnKey === "invoiced" && sortedInfo.order,
      render: (_value, record) => {
        const business = record.businesses ? record.businesses[0] : null;
        const subscription = business?.subscription;
        const price = subscription?.items.data[0].price;
        const product = price?.product as Stripe.Product;
        const invoiceFinalized = business?.invoiceFinalized;
        const isTradeCustomer = price?.metadata.trade === "true";
        const isInvoiced =
          product?.metadata.gift_eligible === "1" || isTradeCustomer;

        if (isTradeCustomer) {
          return (
            <Button
              style={{ paddingTop: 0 }}
              type="ghost"
              disabled={!isInvoiced}
              onClick={() =>
                handleToggleInvoiced({
                  code: business?.code || "",
                  sub: subscription?.id || "",
                })
              }
            >
              {invoiceFinalized ? (
                <CheckCircleFilled style={{ color: "green" }} />
              ) : (
                <CloseCircleFilled style={{ color: "red" }} />
              )}
            </Button>
          );
        } else {
          return <MinusOutlined style={{ color: "lightblue" }} />;
        }
      },
    },
    {
      title: "Actions",
      key: "operation",
      fixed: "right",
      width: 100,
      render: (_value, record) => {
        return (
          <Space>
            <Button
              type="primary"
              onClick={() => {
                history.push({
                  pathname: `/customers/${record.sub}`,
                  state: { customer: record },
                });
              }}
            >
              View
            </Button>
          </Space>
        );
      },
    },
  ];

  return columns;
};
